// horarioProfesor.js — horas en las que trabaja un profesor un día concreto, para rayar en la
// Planificación lo que cae FUERA de su horario (`businessHours` de @conext/calendar). PURO.
//
// COPIA POR VALOR (mismo fichero byte a byte) en el simulador de ventas:
//   apps/conext-ventas/src/simulator/pages/autoescuela/horarioProfesor.js
// fijada por apps/conext-ventas/src/simulator/pages/autoescuela/__tests__/horarioProfesor.copia.test.js.
//
// Fuente ÚNICA de los horarios: c_calendar_availability, la que edita «Configurar horario»
// (Trabajadores → UserScheduleForm → PUT /peluqueria/user-schedule) y la que lee el motor de huecos de
// la reserva directa (c_pedirPracticaFlow/reservaSlots.js → rangesFor). En la app llega por
// GET /peluqueria/workers-schedules → { schedules: {uid: horario}, overrides: {uid: periodos} }.
//   - horario: { lunes: [{start:"08:00", end:"14:00"}, …], "miércoles": […], … } (claves con tilde; se
//     aceptan también sin tilde, como en el motor). Puede llegar como texto JSON.
//   - periodos especiales: [{ start_date:"YYYY-MM-DD", end_date, schedule }]: el primero que cubre la
//     fecha MANDA sobre el horario habitual (mismo criterio que resolveScheduleForDate y rangesFor).
//
// Diferencia consciente con el motor: el motor no ofrece horas en los festivos NACIONALES; aquí no se
// rayan (los festivos y cierres del negocio ya los pinta el calendario desde /holidays).

export const DIAS_HORARIO = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"]; // índice = getDay()
const SIN_TILDE = { "miércoles": "miercoles", "sábado": "sabado" };
const HHMM = /^(\d{1,2}):(\d{2})$/;

function parseMaybeJson(v, def) {
  if (v == null) return def;
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch (_) { return def; }
  }
  return v;
}

const aMinutos = (hhmm) => {
  const m = HHMM.exec(String(hhmm || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 24 && min <= 59 ? h * 60 + min : null;
};

/** «YYYY-MM-DD» en fecha LOCAL (toISOString daría el día UTC, que a medianoche no coincide). */
export function fechaLocal(date) {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const tieneTramos = (horario) => !!horario && typeof horario === "object"
  && Object.values(horario).some((v) => Array.isArray(v) && v.length > 0);

/**
 * Franjas en las que el profesor trabaja ese día, en minutos desde medianoche ([{start,end}], ordenadas).
 *  - `null` si no tiene horario configurado (ni habitual ni periodo especial que cubra ese día): entonces
 *    no se raya nada (un profesor recién creado no sale con la columna entera en gris).
 *  - `[]` si ese día no trabaja (día libre o periodo especial sin horas: vacaciones).
 */
export function horasAbiertasProfesor(horario, periodos, date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const key = fechaLocal(d);
  const lista = parseMaybeJson(periodos, []);
  const especial = (Array.isArray(lista) ? lista : []).find((o) => o && o.schedule && typeof o.schedule === "object"
    && typeof o.start_date === "string" && typeof o.end_date === "string" && o.start_date <= key && key <= o.end_date);
  const base = parseMaybeJson(horario, {});
  if (!especial && !tieneTramos(base)) return null;
  const vigente = especial ? especial.schedule : base;
  const nombre = DIAS_HORARIO[d.getDay()];
  const tramos = vigente[nombre] || vigente[SIN_TILDE[nombre]] || [];
  return (Array.isArray(tramos) ? tramos : [])
    .map((t) => ({ start: aMinutos(t && t.start), end: aMinutos(t && t.end) }))
    .filter((r) => r.start != null && r.end != null && r.end > r.start)
    .sort((a, b) => a.start - b.start);
}

/**
 * Profesor cuyo horario se raya en Día/Semana (sin columnas): el trabajador, el suyo; el director, el
 * filtrado en el panel; y si la vista tiene UN solo profesor (autoescuela de un profesor, o sede con uno),
 * ese, porque entonces no hay ni vista «Por profesor» ni filtro que elegir. Si no, ninguno.
 *
 * @param {{esTrabajador?: boolean, propioId?: string|null, filtro?: string|null, profesores?: Array<{id:string}>}} o
 */
export function profesorParaRayar({ esTrabajador = false, propioId = null, filtro = null, profesores = [] } = {}) {
  if (esTrabajador) return propioId || null;
  if (filtro) return filtro;
  return Array.isArray(profesores) && profesores.length === 1 && profesores[0] ? String(profesores[0].id) : null;
}

/**
 * `businessHours` para @conext/calendar: en cada columna de profesor, sus horas; en Día/Semana sin
 * columnas, las del profesor `profesorFijo` (ver profesorParaRayar). Sin
 * profesor, con «Sin asignar» o con un profesor sin horario → null (nada rayado).
 *
 * @param {{schedules?: Object, overrides?: Object}} horarios  respuesta de /peluqueria/workers-schedules
 * @param {string|null} profesorFijo
 */
export function crearBusinessHoursProfesores(horarios, profesorFijo) {
  const schedules = (horarios && horarios.schedules) || {};
  const overrides = (horarios && horarios.overrides) || {};
  return ({ date, resource } = {}) => {
    const id = resource ? resource.id : profesorFijo;
    if (id == null || !Object.prototype.hasOwnProperty.call(schedules, String(id))) return null;
    return horasAbiertasProfesor(schedules[String(id)], overrides[String(id)], date);
  };
}
